/**
 * client/src/pages/security-centre/components/MasterHeldNextStep.tsx
 *
 * Split out of `ProtectionLevelSummary.tsx` for the 300-line modularity cap
 * (#3268 review round 1 MAJOR 2). Rendered when `resolveProtectionNextStep`
 * returns `{ kind: "master-held" }` — the designated master is incident-held
 * and excluded by the offerable filter, so NEITHER AAL3 basis (hardware or
 * dual) can complete (`step-up/begin.ts`'s `HELD_MASTER_USE_TRANSFER`).
 *
 * Amber, never red (CLAUDE.md) — the incident is over; this is a standing
 * state waiting on the owner, not an error they made.
 *
 * ROUTE FIX (review round 2 MAJOR 1): this card renders inside
 * `ProtectionLevelSummary` → `client/src/app/aal-gate-panel.tsx` — an
 * AAL2/AAL3-gated route, which is NOT `/security-keys` and can be an ORG
 * route. A bare `href="#master-passkey"` therefore scrolled nowhere (no such
 * element on THIS page) and, in org context, `/security-keys` alone would
 * still be a dead link (`RouteGuard` bounces an org-context visitor off that
 * personal-only route). Fixed the same way `HeldKeyPostureNotice` fixes the
 * identical problem: link straight to `/security-keys#master-passkey` in
 * personal context; in org context, reuse (not duplicate)
 * `useSwitchToPersonalThenNavigate` to switch first, then land on that same
 * anchor.
 */
import { Link } from "wouter";
import { useSwitchToPersonalThenNavigate } from "@/lib/use-switch-to-personal-then-navigate";
import { CTA_CLASSES } from "@/lib/cta-classes";
import { MASTER_PANEL_ANCHOR } from "./credential-held-notice";

const MASTER_PANEL_HREF = `/security-keys#${MASTER_PANEL_ANCHOR}`;

export function MasterHeldNextStep() {
  const { inOrgContext, switching, error, run } = useSwitchToPersonalThenNavigate(MASTER_PANEL_HREF);

  return (
    <div data-testid="protection-next-step-master-held" className="space-y-1.5">
      <p className="text-warning m-0">
        Your master passkey is on hold after a security incident.
      </p>
      {inOrgContext ? (
        <>
          <button
            type="button"
            onClick={run}
            disabled={switching}
            data-testid="master-held-switch-to-personal"
            className={CTA_CLASSES}
          >
            {switching ? "Switching…" : "Switch to your personal account to transfer master"}
          </button>
          {error && (
            <p role="alert" data-testid="master-held-switch-error" className="text-warning m-0">
              {error}
            </p>
          )}
        </>
      ) : (
        <Link href={MASTER_PANEL_HREF} className="text-interactive hover:underline">
          Transfer master to another passkey
        </Link>
      )}
    </div>
  );
}

export default MasterHeldNextStep;
